import { NudosMaskedNumberInput } from 'components/NudosComponents';
import { Controller, ControllerRenderProps, useFormContext, useWatch } from 'react-hook-form';
import { IOrderPriceEditionForm } from '../../OrderPriceEdition.types';
import { useEffect, useState } from 'react';
import { CURRENCIES } from 'types/bussines';

import './EditableOrderRow.scss';

interface IEditableOrderRowProps {
  registerReference: `sales.${number}`;
}

enum LOCAL_PRICE_INPUT_MODE {
  HIDDEN = 0,
  READONLY = 1,
  EDITABLE = 2
}

const EditableOrderRow = ({ registerReference }: IEditableOrderRowProps) => {
  const { control, getValues, setValue } = useFormContext<IOrderPriceEditionForm>();
  const exchangeRate = useWatch({ control, name: 'exchangeRate' });
  const saleProduct = useWatch({ control, name: registerReference });

  const [localPriceInputMode, setLocalPriceInputMode] = useState<LOCAL_PRICE_INPUT_MODE>(LOCAL_PRICE_INPUT_MODE.HIDDEN);
  const showLocalPriceInput =
    localPriceInputMode === LOCAL_PRICE_INPUT_MODE.EDITABLE || localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY;

  const getLocalPriceInputMode = () => {
    const currency = getValues('currency');
    switch (currency) {
      case CURRENCIES.USD: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.HIDDEN);
        break;
      }
      case CURRENCIES.ARS: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
        break;
      }
      case CURRENCIES.BRL: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
        break;
      }
      default: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.READONLY);
        break;
      }
    }
  };

  const handleBlurLocalPrice = (
    text: string = '',
    field: ControllerRenderProps<IOrderPriceEditionForm, `sales.${number}`>
  ) => {
    let newLocalPrice = Number(text) || 0;
    if (localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.EDITABLE)
      return field.onChange({ ...field.value, saleValue: newLocalPrice });
    const newExchangeRate = Math.ceil((newLocalPrice / Number(field.value.saleValueUsd)) * 100) / 100;
    newLocalPrice = Number(field.value.saleValueUsd) * newExchangeRate;
    field.onChange({ ...field.value, saleValue: newLocalPrice });
  };

  const handleObservableServiceFields = () => {
    const { saleValueUsd } = saleProduct || {};
    const localPrice = Number(saleValueUsd) * exchangeRate;
    if (saleValueUsd !== null && exchangeRate && localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.HIDDEN) {
      setValue(registerReference, {
        ...saleProduct,
        saleValue: localPrice
      });
    }
  };

  useEffect(() => {
    handleObservableServiceFields();
  }, [saleProduct.saleValueUsd, exchangeRate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLocalPriceInputMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Controller
      name={`${registerReference}`}
      control={control}
      render={({ field }) => (
        <section className="editableServiceRow">
          <NudosMaskedNumberInput
            key={`EditableOrderRow-${registerReference}-1`}
            isFilled={true}
            label={'Precio con IVA en USD'}
            placeholder="$0.00 USD"
            handleChange={text => field.onChange({ ...field.value, saleValueUsd: text })}
            numberOfDecimals={2}
            maskPrefix="$"
            maskSufix=" USD"
            currentValue={field.value.saleValueUsd ? String(field.value.saleValueUsd) : ''}
          />
          {showLocalPriceInput && (
            <NudosMaskedNumberInput
              key={`EditableServiceRow-${registerReference}-3`}
              isFilled={true}
              label={`Precio con IVA en ${getValues('currency')}`}
              placeholder={`$0.00 ${getValues('currency')}`}
              handleBlur={text => handleBlurLocalPrice(text, field)}
              numberOfDecimals={2}
              maskPrefix="$"
              maskSufix={` ${getValues('currency')}`}
              currentValue={field.value.saleValue ? Number(field.value.saleValue).toFixed(2) : ''}
              customClassName="localPriceInput"
              isDeactivated={localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY}
            />
          )}
        </section>
      )}
    />
  );
};

export default EditableOrderRow;
